/**
 * The catalogue's facets, as the rail DRAWS them — four groups of chips, the row of active
 * selections above them, and the one dispatch that a chip press goes through.
 *
 * ── Why this exists rather than four handlers in the component ────────────────────────────────
 * The 02.08 KANON gallery draws the facets as a wall of chips inside four collapsible groups, each
 * group stating what is ticked in it ("PARTIE  Klatka · Barki") and a row above them repeating every
 * selection as a removable chip. Three decisions come with that drawing and none of them is
 * rendering:
 *
 *   · which of a group's chips are ticked, and how the group WORDS that in one line;
 *   · which selections appear in the removable row — and that `origin: all` is not one of them,
 *     because "everything" is the absence of a filter rather than a filter to take off;
 *   · which facet a chip belongs to, so one press can be dispatched without the caller holding four
 *     typed toggles that each know a different value type.
 *
 * `exerciseCatalogue` already answers "which options exist and how many movements carry each"; this
 * answers "what the rail puts on screen and what a press means". Keeping the two apart is what lets
 * the component be a `map` over data.
 *
 * ── Chip identity is a STRING, deliberately ──────────────────────────────────────────────────
 * The four facets are keyed on three different value types — a muscle slug, an equipment id, a type
 * slug, an origin slug. Carrying them as one union would make every caller re-narrow it; carrying
 * them as strings and resolving back inside `toggleExerciseFacet` puts the one place that has to
 * know which facet parses how in the module that owns the facets. An id the catalogue never offered
 * is ignored rather than written into the query, so a stale press cannot corrupt it.
 *
 * ── Labels arrive as functions ───────────────────────────────────────────────────────────────
 * `model/` may not import i18next, and three of the four facets label themselves differently:
 * muscles and types from i18n, equipment from BACKEND DATA that is already localised, origin from
 * i18n again. The caller supplies the resolvers; this module decides what to resolve and in what
 * order.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import {
  EXERCISE_ORIGINS,
  toggleFacetValue,
  type ExerciseCatalogueView,
  type ExerciseOrigin,
  type ExerciseQuery,
} from './exerciseCatalogue';
import { EXERCISE_TYPE_KEYS, type ExerciseTypeKey } from './exerciseType';
import { ALL_MUSCLE_GROUPS, type MuscleGroup } from './muscleGroup';

/** The four groups the gallery draws, in the order it draws them. */
export type ExerciseFacetKey = 'muscles' | 'equipment' | 'types' | 'origin';

export const EXERCISE_FACET_KEYS: readonly ExerciseFacetKey[] = [
  'muscles',
  'equipment',
  'types',
  'origin',
];

/** One chip inside a group. */
export interface ExerciseFacetChipView {
  /** Identity within its facet — what `toggleExerciseFacet` takes back. */
  readonly id: string;
  readonly label: string;
  /** How many movements in the WHOLE catalogue carry this value. Never the result count. */
  readonly count: number;
  readonly isSelected: boolean;
  /**
   * The muscle group this chip stands for, so the rail can paint the gallery's coloured square.
   * `null` for every other facet — a group that is not a body part has no colour to carry.
   */
  readonly muscle: MuscleGroup | null;
  /**
   * The option's own photograph, so the drawer's `Sprzęt` tiles can draw the piece rather than its
   * initial. `null` for every other facet — a type or an origin is a word, not a thing with a
   * picture of it — and `null` for a piece the catalogue named without one.
   */
  readonly imageUrl: string | null;
}

export interface ExerciseFacetGroupView {
  readonly facet: ExerciseFacetKey;
  readonly label: string;
  /**
   * What is ticked, in one line: the selected labels joined, or the "everything" word when nothing
   * narrows. The gallery draws the second in quieter ink, which is what `isNarrowing` is for.
   */
  readonly summary: string;
  /** False when the group is not narrowing anything — the summary is then the "everything" word. */
  readonly isNarrowing: boolean;
  readonly chips: readonly ExerciseFacetChipView[];
}

/** One removable chip in the row above the groups. */
export interface ActiveExerciseFilterView {
  readonly facet: ExerciseFacetKey;
  readonly id: string;
  readonly label: string;
}

/** Everything the four groups need to say themselves in the reader's language. */
export interface ExerciseFacetLabels {
  readonly group: (facet: ExerciseFacetKey) => string;
  readonly muscle: (muscle: MuscleGroup) => string;
  readonly type: (type: ExerciseTypeKey) => string;
  readonly origin: (origin: ExerciseOrigin) => string;
  /** The word a group uses when nothing in it is ticked — the gallery's "Wszystkie". */
  readonly everything: string;
}

/** The gallery joins a group's ticked labels with a middot. */
const SUMMARY_SEPARATOR = ' · ';

function summarise(
  chips: readonly ExerciseFacetChipView[],
  everything: string,
): { summary: string; isNarrowing: boolean } {
  const selected = chips.filter((chip) => chip.isSelected);
  if (selected.length === 0) return { summary: everything, isNarrowing: false };
  return {
    summary: selected.map((chip) => chip.label).join(SUMMARY_SEPARATOR),
    isNarrowing: true,
  };
}

/**
 * The four groups, in the gallery's order, each with its chips and its one-line summary.
 *
 * A group the catalogue has no options for is still returned, with no chips: the gallery draws four
 * rows and a row that comes and goes as the catalogue changes is a layout that jumps. What a group
 * with no chips does NOT do is claim a selection — `summarise` reads the chips, so an empty group
 * always says the "everything" word.
 */
export function exerciseFacetGroups(
  catalogue: ExerciseCatalogueView,
  labels: ExerciseFacetLabels,
): ExerciseFacetGroupView[] {
  const groups: ExerciseFacetGroupView[] = [];

  const build = (
    facet: ExerciseFacetKey,
    chips: readonly ExerciseFacetChipView[],
  ): ExerciseFacetGroupView => ({
    facet,
    label: labels.group(facet),
    ...summarise(chips, labels.everything),
    chips,
  });

  groups.push(
    build(
      'muscles',
      catalogue.muscles.map((option) => ({
        id: option.value,
        label: labels.muscle(option.value),
        count: option.count,
        isSelected: option.isSelected,
        muscle: option.value,
        imageUrl: null,
      })),
    ),
  );

  groups.push(
    build(
      'equipment',
      catalogue.equipment.map((option) => ({
        id: String(option.value),
        // Backend DATA, already resolved into the reader's language. Never i18n.
        label: option.name,
        count: option.count,
        isSelected: option.isSelected,
        muscle: null,
        imageUrl: option.imageUrl,
      })),
    ),
  );

  groups.push(
    build(
      'types',
      catalogue.types.map((option) => ({
        id: option.value,
        label: labels.type(option.value),
        count: option.count,
        isSelected: option.isSelected,
        muscle: null,
        imageUrl: null,
      })),
    ),
  );

  // Origin is single-select and `all` is always offered — it is how a trainer gets back. It is
  // never shown as SELECTED, because the group's summary would then read "Wszystkie" twice: once as
  // the ticked chip's label and once as the word for "nothing is ticked".
  groups.push(
    build(
      'origin',
      catalogue.origins.map((option) => ({
        id: option.value,
        label: labels.origin(option.value),
        count: option.count,
        isSelected: option.isSelected && option.value !== 'all',
        muscle: null,
        imageUrl: null,
      })),
    ),
  );

  return groups;
}

/**
 * Every selection, as a removable chip — the row the gallery draws above the groups.
 *
 * In the groups' own order, so a chip does not move when an unrelated facet is touched. `origin:
 * all` is deliberately absent: it is the default, and a chip offering to remove "everything" would
 * be a control whose × does nothing visible.
 */
export function activeExerciseFilters(
  catalogue: ExerciseCatalogueView,
  labels: ExerciseFacetLabels,
): ActiveExerciseFilterView[] {
  return exerciseFacetGroups(catalogue, labels).flatMap((group) =>
    group.chips
      .filter((chip) => chip.isSelected)
      .map((chip) => ({ facet: group.facet, id: chip.id, label: chip.label })),
  );
}

/**
 * A chip press, applied to the query.
 *
 * Multi-select facets toggle; ORIGIN sets, because it is single-select — pressing the ticked origin
 * again would otherwise leave the catalogue with no origin at all, which is not a state the query
 * can express. An id no facet recognises is returned unchanged rather than written through.
 */
export function toggleExerciseFacet(
  query: ExerciseQuery,
  facet: ExerciseFacetKey,
  id: string,
): ExerciseQuery {
  switch (facet) {
    case 'muscles': {
      // Resolved against the taxonomy rather than cast: a chip id is a string off the DOM, and an
      // unrecognised one written straight into the query would filter every movement out with no
      // chip on screen to explain it.
      const muscle = ALL_MUSCLE_GROUPS.find((candidate) => candidate === id);
      if (muscle === undefined) return query;
      return { ...query, muscles: toggleFacetValue(query.muscles, muscle) };
    }
    case 'equipment': {
      const parsed = Number(id);
      if (!Number.isInteger(parsed)) return query;
      return { ...query, equipment: toggleFacetValue(query.equipment, parsed) };
    }
    case 'types': {
      const type = EXERCISE_TYPE_KEYS.find((candidate) => candidate === id);
      if (type === undefined) return query;
      return { ...query, types: toggleFacetValue(query.types, type) };
    }
    case 'origin': {
      const origin = EXERCISE_ORIGINS.find((candidate) => candidate === id);
      if (origin === undefined) return query;
      return { ...query, origin };
    }
  }
}

/**
 * Take one selection OFF — what the × on an active chip does, and what pressing an already-ticked
 * chip inside a group does.
 *
 * Removing an ORIGIN means returning to `all` rather than toggling, for the same reason the press
 * above sets rather than toggles.
 */
export function removeExerciseFilter(
  query: ExerciseQuery,
  facet: ExerciseFacetKey,
  id: string,
): ExerciseQuery {
  if (facet === 'origin') return { ...query, origin: 'all' };
  return toggleExerciseFacet(query, facet, id);
}

/**
 * Select or drop one of the trainer's tags, by its normalised key — what a chip in the tag bar and
 * a row in the filter panel's „Tagi" tab both dispatch, so the two cannot drift apart (R23).
 *
 * Its own entry point rather than a fifth `ExerciseFacetKey`: tags are filtered on `/cwiczenia`
 * alone, and `exerciseFacetGroups` feeds the builder's drawer and the picker too, which must not
 * grow a tag group. A key that normalises to nothing is no tag and changes nothing.
 */
export function toggleExerciseTag(query: ExerciseQuery, key: string): ExerciseQuery {
  if (key.trim() === '') return query;
  return { ...query, tags: toggleFacetValue(query.tags, key) };
}

/** Drop every selected tag and nothing else — the tag bar's „Wyczyść". */
export function clearExerciseTags(query: ExerciseQuery): ExerciseQuery {
  return { ...query, tags: [] };
}
